/**
 * Powerstep state: on `cart:added`, opens the powerstep drawer if the product has the trigger tag and filters leave
 * something to show, else the cart drawer; then refetches cart data. Filter values and `data-pending-*` attributes
 * are written synchronously BEFORE dispatching `powerstep:triggered` (onTriggered reads them during dispatch).
 * `/cart/add.js` returns the line's TOTAL quantity; the added amount is that minus the pre-add theme.cart quantity.
 */
import type { App } from '@grafikr/components';
import type { CartAdd } from '@grafikr/shopify-typescript/type/endpoints/Cart';
import type { PreStep, Step } from 'components/powerstep-drawer';

import Cart from 'utils/cart';
import { shouldOpenPowerstep } from 'utils/powerstep-filter';

type AccessoriesMap = Record<string, { eligible: boolean; title: string; thumbnails: string[] }>;
type CuttingServiceData = {
  eligibility_map: Record<string, boolean>;
  max_length_map: Record<string, number | null>;
  entity_map: Record<string, { singular: string; plural: string }>;
};

type PowerstepSectionSettings = {
  trigger_tag?: string;
  section_filter_attribute?: string;
  section_manual_filter_value?: string;
  pre_step?: PreStep;
  steps?: Step[];
};

type CartQuantityPriceBreaksData = {
  customerB2b: boolean;
  variants: Record<
    string,
    {
      price: number | string;
      compareAtPrice: number | string | null;
      quantityPriceBreaksConfigured: boolean;
      breaks: Array<{ minimumQuantity: number; price: number | string }>;
    }
  >;
};

type CartDeliveryFetchedPayload = {
  variant_days_map: Record<string, { min: number; max: number } | null>;
};

export default ({ context }: App) => {
  const fetchSectionAsDocument = async (sectionId: string) => {
    const response = await fetch(`${theme.routes.root}?section_id=${sectionId}`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      return null;
    }

    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const readJsonFromScriptElement = <T>(document: Document, selector: string) => {
    const content = document.querySelector<HTMLElement>(selector)?.textContent;
    if (!content) {
      return null;
    }

    return JSON.parse(content) as T;
  };

  const fetchAccessoriesEligibility = async () => {
    try {
      const section = await fetchSectionAsDocument('accessories-data');
      if (!section) {
        return null;
      }

      return readJsonFromScriptElement<AccessoriesMap>(section, '[data-accessories-eligibility]');
    } catch {
      return null;
    }
  };

  const fetchCuttingServiceData = async () => {
    try {
      const section = await fetchSectionAsDocument('cutting-service-data');
      if (!section) {
        return null;
      }

      const marketEnabled = readJsonFromScriptElement<boolean>(
        section,
        '[data-cutting-service-market-enabled]',
      );
      if (marketEnabled === false) {
        return null;
      }

      const eligibilityMap = readJsonFromScriptElement<Record<string, boolean>>(
        section,
        '[data-cutting-service-eligibility]',
      );
      const maxLengthMap = readJsonFromScriptElement<Record<string, number | null>>(
        section,
        '[data-cutting-service-max-length]',
      );
      const entityMap = readJsonFromScriptElement<
        Record<string, { singular: string; plural: string }>
      >(section, '[data-cutting-service-entity]');
      if (!eligibilityMap || !maxLengthMap) {
        return null;
      }

      return {
        eligibility_map: eligibilityMap,
        max_length_map: maxLengthMap,
        entity_map: entityMap ?? {},
      } satisfies CuttingServiceData;
    } catch {
      return null;
    }
  };

  const fetchCartPriceBreaksData = async () => {
    try {
      const section = await fetchSectionAsDocument('cart-price-breaks-data');
      if (!section) {
        return null;
      }

      return readJsonFromScriptElement<CartQuantityPriceBreaksData>(
        section,
        '[data-cart-quantity-price-breaks]',
      );
    } catch {
      return null;
    }
  };

  const fetchCartDeliveryData = async () => {
    try {
      const section = await fetchSectionAsDocument('cart-delivery-data');
      if (!section) {
        return null;
      }

      const variantDaysMap = readJsonFromScriptElement<
        CartDeliveryFetchedPayload['variant_days_map']
      >(section, '[data-cart-delivery-variant-days]');

      if (!variantDaysMap) {
        return null;
      }

      return { variant_days_map: variantDaysMap } satisfies CartDeliveryFetchedPayload;
    } catch {
      return null;
    }
  };

  const fetchProductTags = async (handle: string): Promise<string[]> => {
    try {
      const response = await fetch(`${theme.routes.root}products/${handle}.js`);
      if (!response.ok) {
        return [];
      }

      const product = await response.json();
      return Array.isArray(product.tags) ? product.tags : [];
    } catch {
      return [];
    }
  };

  const extractAddedItemHandle = (cartAdd: CartAdd): string | null => {
    if ('handle' in cartAdd) {
      return cartAdd.handle;
    }
    if ('items' in cartAdd && cartAdd.items.length > 0) {
      return cartAdd.items[0]?.handle ?? null;
    }
    return null;
  };

  const extractAddedItemVariantId = (cartAdd: CartAdd): number | null => {
    if ('variant_id' in cartAdd) return (cartAdd as { variant_id: number }).variant_id ?? null;
    if ('items' in cartAdd && cartAdd.items.length > 0) return cartAdd.items[0]?.variant_id ?? null;
    return null;
  };

  const calculateQuantityAdded = (cartAdd: CartAdd): number => {
    let responseQuantity: number;
    if ('quantity' in cartAdd) {
      responseQuantity = (cartAdd as { quantity: number }).quantity ?? 1;
    } else if ('items' in cartAdd && cartAdd.items.length > 0) {
      responseQuantity = cartAdd.items[0]?.quantity ?? 1;
    } else {
      return 1;
    }

    const variantId = extractAddedItemVariantId(cartAdd);
    if (variantId && theme.cart?.items) {
      const existing = theme.cart.items.find((item) => item.variant_id === variantId);
      const existingQty = existing?.quantity ?? 0;
      return Math.max(1, responseQuantity - existingQty);
    }

    return responseQuantity;
  };

  const fetchTriggerProductFilterValues = async (
    handle: string,
    sectionId: string,
  ): Promise<Record<string, string>> => {
    const url = `${theme.routes.root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`;
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) return {};
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return readJsonFromScriptElement<Record<string, string>>(doc, '[data-filter-values]') ?? {};
    } catch {
      return {};
    }
  };

  const checkIfPowerstepShouldTrigger = async (cartAdd: CartAdd) => {
    const handle = extractAddedItemHandle(cartAdd);
    if (!handle) return false;

    const allPowerstepEls = Array.from(
      document.querySelectorAll<HTMLElement>('[data-component="powerstep-drawer"]'),
    );
    if (!allPowerstepEls.length) return false;

    const tags = await fetchProductTags(handle);

    let matchingEl: HTMLElement | null = null;
    let matchingSettings: PowerstepSectionSettings | null = null;
    for (const el of allPowerstepEls) {
      const settingsEl = el.querySelector<HTMLElement>('[data-settings]');
      if (!settingsEl?.textContent) continue;
      try {
        const parsed = JSON.parse(settingsEl.textContent) as PowerstepSectionSettings;
        if (parsed.trigger_tag && tags.includes(parsed.trigger_tag)) {
          matchingEl = el;
          matchingSettings = parsed;
          break;
        }
      } catch {
        continue;
      }
    }

    if (!matchingEl || !matchingSettings) return false;

    const sectionId = matchingEl.dataset.sectionId ?? 'powerstep-drawer';
    const filter_values = await fetchTriggerProductFilterValues(handle, sectionId);

    const preStepOptions = matchingSettings.pre_step?.options ?? [];
    const filterCtx = {
      sectionFilterAttribute: matchingSettings.section_filter_attribute ?? '',
      sectionManualFilterValue: matchingSettings.section_manual_filter_value ?? '',
      steps: matchingSettings.steps ?? [],
      triggerValues: filter_values,
    };

    if (!shouldOpenPowerstep(preStepOptions, filterCtx)) {
      return false;
    }

    const variantId = extractAddedItemVariantId(cartAdd);
    for (const el of allPowerstepEls) {
      el.dataset.pendingTriggered = el === matchingEl ? 'true' : 'false';
    }
    matchingEl.dataset.pendingFilterValues = JSON.stringify(filter_values);
    if (variantId != null) {
      matchingEl.dataset.pendingVariantId = String(variantId);
    } else {
      delete matchingEl.dataset.pendingVariantId;
    }

    context.dispatchEvent('powerstep:triggered', {
      tags,
      handle,
      variant_id: extractAddedItemVariantId(cartAdd),
      filter_values,
      quantity_added: calculateQuantityAdded(cartAdd),
      section_id: sectionId,
    });
    return true;
  };

  const fetchCartAndRelatedData = async () => {
    const [{ data }, accessoriesMap, cuttingServiceData, cartPriceBreaksData, cartDeliveryData] =
      await Promise.all([
        Cart.get(),
        fetchAccessoriesEligibility(),
        fetchCuttingServiceData(),
        fetchCartPriceBreaksData(),
        fetchCartDeliveryData(),
      ]);

    if (data) {
      theme.cart = data;
      if (accessoriesMap) {
        context.dispatchEvent('accessories:fetched', accessoriesMap);
      }
      if (cuttingServiceData) {
        context.dispatchEvent('cutting-service:fetched', cuttingServiceData);
      }
      if (cartPriceBreaksData) {
        context.dispatchEvent('cart-price-breaks:fetched', cartPriceBreaksData);
      }
      if (cartDeliveryData) {
        context.dispatchEvent('cart-delivery:fetched', cartDeliveryData);
      }
      context.dispatchEvent('cart:fetched', data);
    }
  };

  document.addEventListener('cart:added', async ({ detail: cartAdd }) => {
    const powerstepTriggered = await checkIfPowerstepShouldTrigger(cartAdd);
    if (!powerstepTriggered) {
      context.dispatchEvent('cart:open', undefined);
    }
    await fetchCartAndRelatedData();
  });
  document.addEventListener('cart:updated', fetchCartAndRelatedData);
};
